import React, { useState, useEffect, useCallback } from 'react';
import CoachAddPaymentModal from '../CoachAddPaymentModal';
import axios from 'axios';
import { apiEndpoints, handleApiError } from '../../services/api';
import { createApiUrl, createApiUrlWithPrefix, getUserImageUrl, API_BASE_URL } from '../../config/api';
import { useUserCounts } from '../../contexts/UserCountsContext';
import { toast } from 'react-hot-toast';
import {
  Search,
  UserPlus,
  Edit,
  Trash2,
  RefreshCw,
  UserCheck,
  Eye,
  ChevronLeft,
  ChevronRight,
  QrCode,
  CreditCard,
  MoreVertical
} from 'lucide-react';
import UserCreationModal from '../UserCreationModal';
import UserEditModal from '../UserEditModal';
import UserDetailsModal from '../UserDetailsModal';

import { useTranslation } from 'react-i18next';
function Coaches() {
  const { t, i18n } = useTranslation();
  const { refreshUserCounts } = useUserCounts();
  const isRTL = i18n.language === 'ar';

  // State management
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [viewingUser, setViewingUser] = useState(null);
  const [coachModalOpen, setCoachModalOpen] = useState(false);
  const [selectedCoach, setSelectedCoach] = useState(null);
const [dropdownOpen, setDropdownOpen] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);
// Fetch coaches
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        role: '2', // Coach role
        _t: Date.now(), // Cache busting parameter
        ...(searchTerm && { search: searchTerm })
      });
      
      const response = await axios.get(createApiUrlWithPrefix(`users?${params}`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const coaches = response.data.users || response.data;
      
      // Debug: Log image paths for coaches
      console.log('🖼️ Coaches data received:', coaches.length, 'coaches');
      coaches.forEach((coach, index) => {
        console.log(`🖼️ Coach ${index + 1}:`, {
          id: coach.id,
          name: coach.name,
          photoPath: coach.photoPath,
          PhotoPath: coach.PhotoPath,
          imagePath: coach.imagePath,
          ImagePath: coach.ImagePath,
          role: coach.role
        });
      });
      
      setUsers(coaches);
      setTotalPages(response.data.totalPages || 1);
      setTotalItems(response.data.totalItems || response.data.length);
    } catch (error) {
      console.error('Failed to fetch coaches:', error);
      toast.error(t('coaches.messages.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, searchTerm]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Listen for the custom event from sidebar
  useEffect(() => {
    const handleOpenAddUserModal = (event) => {
      if (event.detail.role === 'Coach' || event.detail.role === 2) {
        setShowModal(true);
      }
    };

    window.addEventListener('openAddUserModal', handleOpenAddUserModal);
    return () => {
      window.removeEventListener('openAddUserModal', handleOpenAddUserModal);
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen && !event.target.closest('.dropdown-container')) {
        setDropdownOpen(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  // Event handlers
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(user => user.id));
    }
  };

  const handleUserCreated = async () => {
    console.log('🔄 User created, refreshing list...');
    await fetchUsers(); // Refresh the list
    setRefreshKey(prev => prev + 1); // Force component refresh
    console.log('✅ Users list refreshed after creation');
  
    // Refresh sidebar counts
    refreshUserCounts();};

  const handleEditUser = (user) => {
    setEditingUser(user);
    setShowEditModal(true);
  };

  const handleViewUserDetails = (user) => {
    setViewingUser(user);
    setShowDetailsModal(true);
  };

  // Dropdown functions
  const handleDropdownToggle = (userId) => {
    setDropdownOpen(dropdownOpen === userId ? null : userId);
  };

  const handleAction = (action, user) => {
    setDropdownOpen(null);
    switch (action) {
      case 'view':
        handleViewUserDetails(user);
        break;
      case 'edit':
        handleEditUser(user);
        break;
      case 'payments':
        setSelectedCoach(user);
        setCoachModalOpen(true);
        break;
      case 'print':
        handlePrintBadge(user);
        break;
      case 'delete':
        handleDeleteUser(user.id);
        break;
      default:
        break;
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      try {
        await axios.delete(createApiUrlWithPrefix(`users/${userId}`), {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        toast.success('User deleted successfully');
        
        const currentUsersCount = users.length;
        
        if (currentUsersCount === 1 && currentPage > 1) {
          const newPage = Math.max(currentPage - 1, 1);
          setCurrentPage(newPage);
        } else {
          await fetchUsers();
        }
        
        setRefreshKey(prev => prev + 1);
        refreshUserCounts();
      } catch (error) {
        console.error('Failed to delete user:', error);
        toast.error('Failed to delete user');
      }
    }
  };

  const handleUserUpdated = () => {
    setShowEditModal(false);
    setEditingUser(null);
    fetchUsers();
    // Refresh sidebar counts (in case role changed)
    refreshUserCounts();
  };

  // Print badge function
  const handlePrintBadge = async (user) => {
    try {
      const response = await axios.get(createApiUrlWithPrefix(`users/${user.id}/qr-code`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const qrData = response.data.userGuid || response.data.qrCode || `EC_${user.id}`;
      
      const birthDate = user.birthDate && user.birthDate !== null
        ? new Date(user.birthDate).toLocaleDateString('en-US', {
            year: 'numeric',
            month: '2-digit', 
            day: '2-digit'
          })
        : 'Not provided';
      
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html>
          <head>
            <title>Badge - ${user.firstName} ${user.lastName}</title>
            <style>
              body { 
                font-family: 'Segoe UI', Arial, sans-serif; 
                margin: 0; 
                padding: 20px; 
                background: white;
                display: flex;
                justify-content: center;
                align-items: center;
                min-height: 100vh;
              }
              .badge { 
                width: 500px; 
                height: 420px; 
                border: 2px solid #2c3e50; 
                margin: 0 auto;
                background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
                border-radius: 15px;
                box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                position: relative;
                overflow: hidden;
              }
              .header { 
                background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
                color: white;
                padding: 15px 25px;
                text-align: center;
                font-weight: bold;
                font-size: 18px;
                letter-spacing: 1px;
              }
              .content { 
                padding: 25px;
                display: flex;
                gap: 20px;
                height: calc(100% - 70px);
              }
              .left-section { 
                flex: 1;
                display: flex;
                flex-direction: column;
                gap: 10px;
              }
              .user-photo { 
                width: 100px; 
                height: 100px; 
                border-radius: 50%; 
                border: 3px solid #3b82f6;
                object-fit: cover;
                margin-bottom: 15px;
              }
              .user-photo-placeholder { 
                width: 100px; 
                height: 100px; 
                border-radius: 50%; 
                border: 3px solid #3b82f6;
                background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
                display: flex;
                align-items: center;
                justify-content: center;
                font-weight: bold;
                font-size: 32px;
                color: #3b82f6;
                margin-bottom: 15px;
              }
              .user-name { 
                font-size: 22px; 
                font-weight: bold; 
                color: #2c3e50;
                margin-bottom: 8px;
              }
              .user-details { 
                font-size: 14px; 
                color: #5a6c7d;
                line-height: 1.6;
              }
              .detail-item { 
                margin-bottom: 8px;
                display: flex;
                align-items: center;
                gap: 8px;
              }
              .right-section { 
                flex: 1;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
              }
              .qr-container { 
                width: 140px; 
                height: 140px; 
                border: 2px solid #3b82f6;
                border-radius: 10px;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                background: white;
                margin-bottom: 15px;
              }
              .qr-code { 
                width: 120px;
                height: 120px;
                display: flex;
                align-items: center;
                justify-content: center;
              }
              .qr-label { 
                font-size: 12px; 
                color: #3b82f6;
                font-weight: bold;
                text-align: center;
                margin-bottom: 10px;
              }
              .role-badge { 
                background: linear-gradient(135deg, #10b981 0%, #059669 100%);
                color: white;
                padding: 6px 16px;
                border-radius: 20px;
                font-size: 12px;
                font-weight: bold;
                text-transform: uppercase;
                letter-spacing: 0.5px;
              }
              @media print {
                body { margin: 0; padding: 10px; }
                .badge { margin: 0; box-shadow: none; }
              }
            </style>
          </head>
          <body>
            <div class="badge">
              <div class="header">EQUESTRIAN CLUB MEMBER</div>
              <div class="content">
                <div class="left-section">
                  ${(user.photoPath || user.imagePath) ?
                    `<img src="${API_BASE_URL}/${user.photoPath || user.imagePath}" alt="User Photo" class="user-photo" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';" />` :
                    ''
                  }
                  <div class="user-photo-placeholder" style="${(user.photoPath || user.imagePath) ? 'display: none;' : 'display: flex;'}">
                    ${user.firstName?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  
                  <div class="user-name">${user.firstName} ${user.lastName}</div>
                  <div class="user-details">
                    <div class="detail-item">📅 Birth Date: ${birthDate}</div>
                    <div class="detail-item">🩸 Blood Type: ${user.bloodType || 'N/A'}</div>
                  </div>
                </div>
                
                <div class="right-section">
                  <div class="qr-container">
                    <div class="qr-code" id="qr-code-${user.id}"></div>
                  </div>
                  <div class="qr-label">SCAN QR CODE</div>
                  <div class="role-badge">COACH</div>
                </div>
              </div>
            </div>
            <script src="https://cdn.jsdelivr.net/npm/qrcode@1.5.3/build/qrcode.min.js"></script>
            <script>
              function generateQRCode() {
                const qrElement = document.getElementById('qr-code-${user.id}');
                if (qrElement && typeof QRCode !== 'undefined') {
                  try {
                    const canvas = document.createElement('canvas');
                    qrElement.appendChild(canvas);
                    QRCode.toCanvas(canvas, '${qrData}', {
                      width: 120,
                      height: 120,
                      margin: 1,
                      color: { dark: '#2c3e50', light: '#ffffff' },
                      errorCorrectionLevel: 'M'
                    });
                  } catch (err) {
                    console.error('QR Code generation error:', err);
                  }
                }
              }
              let attempts = 0;
              const maxAttempts = 10;
              function tryGenerateQR() {
                if (typeof QRCode !== 'undefined') {
                  generateQRCode();
                } else if (attempts < maxAttempts) {
                  attempts++;
                  setTimeout(tryGenerateQR, 200);
                }
              }
              tryGenerateQR();
            </script>
          </body>
        </html>
      `);
      
      printWindow.document.close();
      printWindow.print();
      
      toast.success('Badge opened for printing');
    } catch (error) {
      console.error('Failed to print badge:', error);
      toast.error('Failed to print badge');
    }
  };

  return (
    <>
      <div className="flex flex-col min-h-full bg-neutral-50 pb-8" dir={isRTL ? 'rtl' : 'ltr'} key={refreshKey}>
      <div className="px-4 sm:px-6 lg:px-8 py-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('coaches.title')}</h1>
          <p className="mt-2 sm:mt-3 text-sm sm:text-base text-gray-600">
            {t('coaches.subtitle')}
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="bg-primary-600 text-white px-3 py-2 sm:px-4 rounded-lg hover:bg-primary-700 transition-colors flex items-center min-h-[44px] min-w-[44px] touch-manipulation text-sm sm:text-base"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          <span className="hidden sm:inline">{t('coaches.addCoach')}</span>
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-4 sm:p-6 mt-6">
        <div className="flex flex-col lg:flex-row gap-4 items-center">
          <div className="flex-1 w-full">
            <div className="relative">
              <Search className={`absolute top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 ${isRTL ? 'right-4' : 'left-4'}`} />
              <input
                type="text"
                placeholder={t('coaches.searchPlaceholder')}
                value={searchTerm}
                onChange={handleSearch}
                className={`w-full py-3 px-10 text-base sm:text-lg border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 ${isRTL ? 'pr-11 pl-4' : 'pl-11 pr-4'}`}
              />
            </div>
          </div>

          <div className="flex items-center space-x-3 w-full lg:w-auto">
            <button
              onClick={clearFilters}
              className="flex-1 lg:flex-none justify-center px-6 py-3 text-gray-600 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors font-medium"
            >
              {t('common.clear')}
            </button>
            
            <button
              onClick={fetchUsers}
              className="flex-1 lg:flex-none flex justify-center items-center px-6 py-3 text-gray-600 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
      </div>

      <div className="px-4 sm:px-6 lg:px-8 pb-8">
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={selectedUsers.length === users.length && users.length > 0}
                onChange={handleSelectAll}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mr-3"
              />
              <span className="text-sm font-medium text-gray-700">
                Select All ({selectedUsers.length} of {users.length} selected)
              </span>
            </div>
            {selectedUsers.length > 0 && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500">
                  {selectedUsers.length} coach{selectedUsers.length !== 1 ? 'es' : ''} selected
                </span>
                <button className="px-3 py-1 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors">
                  Delete Selected
                </button>
              </div>
            )}
          </div>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
          {users.map((user) => (
            <div key={user.id} className="relative bg-white rounded-lg shadow-md border border-gray-200 hover:shadow-xl transition-all duration-300 overflow-hidden group">
              <div className="flex items-center justify-between p-4 pb-2">
                <input
                  type="checkbox"
                  checked={selectedUsers.includes(user.id)}
                  onChange={() => handleSelectUser(user.id)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-800">
                  Coach
                </span>
              </div>
              
              <div className="px-4 pb-4">
                <div className="flex justify-center mb-4">
                  {(() => {
                    const imageUrl = getUserImageUrl(user);
                    return imageUrl ? (
                      <img src={imageUrl} alt="Coach" className="w-16 h-16 rounded-full object-cover border-2 border-blue-100 shadow-sm" />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center border-2 border-blue-100 shadow-sm">
                        <span className="text-xl font-bold text-blue-600">{user.name?.charAt(0)?.toUpperCase() || 'C'}</span>
                      </div>
                    );
                  })()}
                </div>
                
                <div className="text-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 truncate">{user.name || 'Unknown Coach'}</h3>
                  <p className="text-sm text-gray-500 truncate">{user.email}</p>
                </div>
                
                <div className="space-y-2 text-xs text-gray-600">
                  {user.phoneNumber && (
                    <div className="flex items-center justify-center">
                      <span className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full font-medium">📞 {user.phoneNumber}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-center">
                    <span className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full font-medium">🏇 Riding Instructor</span>
                  </div>
                </div>
              </div>
              
              <div className="absolute top-4 right-4 dropdown-container">
                <button
                  onClick={() => handleDropdownToggle(user.id)}
                  className="flex items-center justify-center w-8 h-8 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <MoreVertical className="w-4 h-4" />
                </button>
                
                {dropdownOpen === user.id && (
                  <div className="absolute right-0 top-10 z-[9999] w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-1">
                    <button onClick={() => handleAction('view', user)} className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"><Eye className="w-4 h-4 mr-3" />{t('common.view')}</button>
                    <button onClick={() => handleAction('edit', user)} className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"><Edit className="w-4 h-4 mr-3" />{t('common.edit')}</button>
                    <button onClick={() => handleAction('payments', user)} className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"><CreditCard className="w-4 h-4 mr-3" />{t('common.payments')}</button>
                    <button onClick={() => handleAction('print', user)} className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"><QrCode className="w-4 h-4 mr-3" />{t('common.print')}</button>
                    <button onClick={() => handleAction('delete', user)} className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4 mr-3" />{t('common.delete')}</button>
                  </div>
                )}
              </div>
            </div>
          ))}
          </div>
        </div>
      </div>

      <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
          <div className="text-xs sm:text-sm text-gray-700 font-medium">
            Showing <span className="font-bold text-blue-600">{((currentPage - 1) * itemsPerPage) + 1}</span> to <span className="font-bold text-blue-600">{Math.min(currentPage * itemsPerPage, totalItems)}</span> of <span className="font-bold text-blue-600">{totalItems}</span> results
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="flex items-center px-3 py-2 sm:px-4 border border-gray-300 rounded-lg text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-all duration-200 min-h-[44px] touch-manipulation"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Previous</span>
              <span className="sm:hidden">Prev</span>
            </button>
            
            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                if (pageNum > totalPages) return null;
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-2 text-xs sm:text-sm font-medium rounded-lg transition-all duration-200 min-h-[44px] min-w-[44px] touch-manipulation ${
                      currentPage === pageNum
                        ? 'bg-blue-600 text-white shadow-lg'
                        : 'text-gray-700 hover:bg-gray-100 border border-gray-300'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="flex items-center px-3 py-2 sm:px-4 border border-gray-300 rounded-lg text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-all duration-200 min-h-[44px] touch-manipulation"
            >
              <span className="hidden sm:inline">Next</span>
              <span className="sm:hidden">Next</span>
              <ChevronRight className="w-4 h-4 ml-1" />
            </button>
          </div>
        </div>
      </div>

      {/* Add Payment Modal */}
      <CoachAddPaymentModal
        isOpen={coachModalOpen}
        onClose={() => {
          setCoachModalOpen(false);
          setSelectedCoach(null);
        }}
        coach={selectedCoach}
        refreshList={fetchUsers}
      />

      {/* User Creation Modal */}
      <UserCreationModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        role="Coach"
        onUserCreated={handleUserCreated}
      />
      
      {/* User Edit Modal */}
      {editingUser && (
        <UserEditModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingUser(null);
          }}
          user={editingUser}
          onUserUpdated={handleUserUpdated}
          disableRoleEdit={true}
        />
      )}
      
      {/* User Details Modal */}
      {viewingUser && (
        <UserDetailsModal
          isOpen={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false);
            setViewingUser(null);
          }}
          user={viewingUser}
        />
      )}
      </div>
    </>
  );
}

export default Coaches;